import { Button, Col, Form, Input, Row, Space, Tooltip, Select } from 'antd';
import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { getProject, createProject, updateProject } from '../../features/projects/projectSlice';
import { MethodologyType, ProjectType } from '../../types/types';
import { NotificationContext } from '../../providers/NotificationProvider';
import RiskMatrix from './matrix/MatrixList';
import { FileDoneOutlined, FileExcelOutlined, ForkOutlined } from '@ant-design/icons';
import {
  downloadProjectActionsReport,
  downloadProjectNodesReport,
  downloadProjectFullReport,
} from '../../services/reportService';
import { useTranslation } from 'react-i18next';
import { get } from '../../api/base';

const ProjectForm: React.FC = () => {
  const { t } = useTranslation();
  const { project_id } = useParams();
  const [isView, setIsView] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { access } = useSelector((store: any) => store.user);
  const { triggerNotification } = useContext(NotificationContext);
  const [projectBackup, setProjectBackup] = useState<ProjectFormData | null>(null);
  const [form] = Form.useForm();
  const [methodologies, setMethodologies] = useState<MethodologyType[]>([]);
  const [loadingMethodologies, setLoadingMethodologies] = useState(false);
  const [projectMethodologyName, setProjectMethodologyName] = useState<string | null>(null);

  interface ProjectFormData {
    name: string;
    customer: string;
    business_unit: string;
    facility: string;
    description: string;
    node_count?: number;
    methodology?: number | null;
    can_export_actions_report?: boolean;
  }

  const analysisButtonLabel = projectBackup?.node_count === 0 ? t('Iniciar Análisis') : t('Ver Análisis');
  const canExportActionsReport = projectBackup?.can_export_actions_report ?? true;

  useEffect(() => {
    if (project_id && !isNaN(parseInt(project_id))) {
      setIsView(true);
      dispatch(getProject(parseInt(project_id as string)) as any)
        .then((result: any) => {
          if (getProject.fulfilled.match(result)) {
            const projectPayload: any = result.payload;
            const project: ProjectFormData = {
              name: projectPayload.name,
              customer: projectPayload.customer,
              business_unit: projectPayload.business_unit,
              facility: projectPayload.facility,
              description: projectPayload.description,
              node_count: projectPayload.node_count,
              methodology: projectPayload?.methodology?.id ?? null,
              can_export_actions_report: projectPayload?.can_export_actions_report ?? true,
            };
            form.setFieldsValue(project);
            setProjectBackup(project);
            setProjectMethodologyName(projectPayload?.methodology?.name ?? null);
          } else {
            triggerNotification('error', t('Error'), t('Error al cargar el proyecto'), 'bottomRight');
            navigate(`/proyectos/`);
            return;
          }
        })
        .catch((err: any) => {
          triggerNotification('error', t('Error'), t('Error al cargar el proyecto'), 'bottomRight');
        });
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setLoadingMethodologies(true);
    const headers = access ? { Authorization: `Bearer ${access}` } : undefined;
    get('methodologies/', { headers })
      .then((response: any) => {
        setMethodologies(response?.data ?? []);
      })
      .catch(() => {
        setMethodologies([]);
      })
      .finally(() => setLoadingMethodologies(false));
  }, []);

  const onFinish = async (values: ProjectFormData) => {
    if (isEdit) {
      const oldProject: ProjectType = {
        id: parseInt(project_id as string),
        name: values.name,
        customer: values.customer,
        business_unit: values.business_unit,
        facility: values.facility,
        description: values.description,
        methodology: values.methodology ?? null,
      };
      try {
        await dispatch(updateProject(oldProject) as any)
          .then((result: any) => {
            if (updateProject.fulfilled.match(result)) {
              navigate(`/proyectos/`);
            }
          })
          .catch((err: any) => {
            triggerNotification('error', t('Error'), t('Error al crear el proyecto'), 'bottomRight');
          });
      } catch (error) {
        triggerNotification('error', t('Error'), t('Error al crear el proyecto'), 'bottomRight');
      }
    } else {
      const newProject: ProjectType = {
        id: null,
        name: values.name,
        customer: values.customer,
        business_unit: values.business_unit,
        facility: values.facility,
        description: values.description,
        methodology: values.methodology ?? null,
      };
      try {
        await dispatch(createProject(newProject) as any)
          .then((result: any) => {
            if (createProject.fulfilled.match(result)) {
              navigate(`/proyecto/${result.payload.id}/nodos/`);
            }
          })
          .catch((err: any) => {
            triggerNotification('error', t('Error'), t('Error al crear el proyecto'), 'bottomRight');
          });
      } catch (error) {
        triggerNotification('error', t('Error'), t('Error al crear el proyecto'), 'bottomRight');
      }
    }
  };

  const handleDownloadReport = async () => {
    if (!project_id) {
      triggerNotification('error', t('Error'), t('Proyecto inválido para exportar'), 'bottomRight');
      return;
    }

    try {
      await downloadProjectActionsReport(parseInt(project_id as string), projectBackup?.name);
      triggerNotification('success', t('Éxito'), t('Descarga del reporte iniciada'), 'bottomRight');
    } catch (error) {
      triggerNotification('error', t('Error'), t('Error al descargar el reporte'), 'bottomRight');
    }
  };

  const handleDownloadNodesReport = async () => {
    if (!project_id) {
      triggerNotification('error', t('Error'), t('Proyecto inválido para exportar'), 'bottomRight');
      return;
    }

    try {
      await downloadProjectNodesReport(parseInt(project_id as string), projectBackup?.name);
      triggerNotification('success', t('Éxito'), t('Descarga del reporte de nodos iniciada'), 'bottomRight');
    } catch (error) {
      triggerNotification('error', t('Error'), t('Error al descargar el reporte de nodos'), 'bottomRight');
    }
  };

  const handleDownloadFullReport = async () => {
    if (!project_id) {
      triggerNotification('error', t('Error'), t('Proyecto inválido para exportar'), 'bottomRight');
      return;
    }

    try {
      await downloadProjectFullReport(parseInt(project_id as string), projectBackup?.name);
      triggerNotification('success', t('Éxito'), t('Descarga del proyecto completo iniciada'), 'bottomRight');
    } catch (error) {
      triggerNotification('error', t('Error'), t('Error al descargar el proyecto completo'), 'bottomRight');
    }
  };

  return (
    <Row className='content'>
      <Col sm={10}>
        <div className='title-full'>
          <h1>{isView || isEdit ? t('Proyecto') : t('Nuevo Proyecto')}</h1>
          {isView && (
            <Space wrap>
              <Button type='primary' htmlType='button' onClick={() => navigate(`/proyecto/${project_id}/nodos/`)}>
                {analysisButtonLabel}
              </Button>
              <Tooltip title={t('Exportar Lista de Recomendaciones')}>
                <Button
                  type='primary'
                  shape='circle'
                  aria-label={t('Descargar reporte de acciones')}
                  style={{ backgroundColor: '#003270', borderColor: '#003270' }}
                  icon={<FileExcelOutlined style={{ color: '#fff' }} />}
                  disabled={!canExportActionsReport}
                  onClick={handleDownloadReport}
                />
              </Tooltip>
              <Tooltip title={t('Exportar Lista de Nodos')}>
                <Button
                  type='primary'
                  shape='circle'
                  aria-label={t('Descargar lista de nodos')}
                  style={{ backgroundColor: '#003270', borderColor: '#003270' }}
                  icon={<ForkOutlined style={{ color: '#fff' }} />}
                  onClick={handleDownloadNodesReport}
                />
              </Tooltip>
              <Tooltip title={t('Exportar Proyecto Completo')}>
                <Button
                  type='primary'
                  shape='circle'
                  aria-label={t('Descargar proyecto completo')}
                  style={{ backgroundColor: '#003270', borderColor: '#003270' }}
                  icon={<FileDoneOutlined style={{ color: '#fff' }} />}
                  onClick={handleDownloadFullReport}
                />
              </Tooltip>
            </Space>
          )}
        </div>

        <div className='ant-divider'></div>
        <Form form={form} layout='vertical' onFinish={onFinish}>
          <Form.Item hidden>
            <Input disabled />
          </Form.Item>
          <Form.Item
            label={t('Nombre del Proyecto')}
            name='name'
            rules={[{ required: true, message: t('Por favor ingresa el nombre del proyecto') }]}
          >
            <Input placeholder={t('Nombre del proyecto')} readOnly={isView} />
          </Form.Item>

          <Form.Item label={t('Cliente')} name='customer'>
            <Input placeholder={t('Nombre del cliente')} readOnly={isView} />
          </Form.Item>

          <Form.Item label={t('Unidad de negocio')} name='business_unit'>
            <Input placeholder={t('Unidad de negocio')} readOnly={isView} />
          </Form.Item>

          <Form.Item label={t('Instalación')} name='facility'>
            <Input placeholder={t('Nombre de la instalación')} readOnly={isView} />
          </Form.Item>

          <Form.Item label={t('Descripción')} name='description'>
            <Input.TextArea placeholder={t('Descripción del proyecto')} readOnly={isView} />
          </Form.Item>
          {isView && !isEdit ? (
            <Form.Item label={t('Metodología')}>
              <Input
                readOnly
                value={
                  projectMethodologyName ??
                  methodologies.find((methodology) => methodology.id === projectBackup?.methodology)?.name ??
                  ''
                }
              />
            </Form.Item>
          ) : (
            <Form.Item
              label={t('Metodología')}
              name='methodology'
              rules={[{ required: true, message: t('Por favor selecciona una metodología') }]}
            >
              <Select
                disabled={isView}
                loading={loadingMethodologies}
                placeholder={t('Metodología del proyecto')}
                options={methodologies.map((methodology) => ({
                  label: methodology.name,
                  value: methodology.id,
                }))}
              />
            </Form.Item>
          )}

          {!isView && (
            <Form.Item>
              <Button type='primary' htmlType='submit'>
                {t('Guardar')}
              </Button>{' '}
              {isEdit ? (
                <Button
                  type='primary'
                  danger
                  htmlType='button'
                  onClick={() => {
                    form.setFieldsValue(projectBackup);
                    setIsView(true);
                    setIsEdit(false);
                  }}
                >
                  {t('Cancelar')}
                </Button>
              ) : null}
            </Form.Item>
          )}
        </Form>
        {isView && !isEdit ? (
          <Button
            type='primary'
            htmlType='button'
            onClick={() => {
              setIsView(false);
              setIsEdit(true);
            }}
          >
            {t('Editar')}
          </Button>
        ) : null}
      </Col>
      <Col sm={2}></Col>
      <Col sm={12}>
        <div className='map-container'>{project_id && <RiskMatrix project_id={project_id} />}</div>
      </Col>
    </Row>
  );
};

export default ProjectForm;
